<script setup lang="ts">
import { watch, computed } from 'vue'
import { useCurrencyFormaterFromCents } from '@/composable/useCurrencyFormater'
import { isValueWithinErrorMargin } from '@/composable/useToolsFareOptimizer'
import { useI18n } from 'vue-i18n'
import { useFareOptimizerStore } from '@/store/fareOptimizer'
import { storeToRefs } from 'pinia'

const { t } = useI18n()
const store = useFareOptimizerStore()

const { tariffTotalTable, tariffTotalAPI, tableItems } = storeToRefs(store)
const hasTotalError = computed(() => !isValueWithinErrorMargin(tariffTotalTable.value, tariffTotalAPI.value))

const { initDisplayItems } = useFareOptimizerStore()

watch(
  () => store.fareItems,
  (newFareData) => {
    if (newFareData) {
      initDisplayItems()
    }
  },
  { deep: true, immediate: true },
)

const updateFareItem = (key: string, amount: number, pricePerUnit: number, rawValue: number) => {
  store.updateFareItem(key, {
    amount,
    price_per_unit: pricePerUnit,
    value: rawValue,
  })
}

function showItem(pricePerUnit, amount) {
  return pricePerUnit !== 0 || amount !== 0
}
</script>

<template>
  <div class="mt-8 h-max w-9/12 bg-white shadow-4">
    <div class="border-b-2 border-stroke">
      <h1 class="border-stroke p-4 font-satoshi text-base font-bold text-black">
        {{ t('contract_optimizer.invoice.items') }}
      </h1>
    </div>

    <table class="w-full text-left text-sm">
      <thead class="bg-slate-50 font-satoshi text-base font-medium text-black">
        <tr>
          <th scope="col" class="px-4 py-3">{{ t('contract_optimizer.invoice.name') }}</th>
          <th scope="col" class="px-4 py-3">{{ t('contract_optimizer.invoice.amount') }}</th>
          <th scope="col" class="px-4 py-3">{{ t('contract_optimizer.invoice.unit_price') }}</th>
          <th scope="col" class="px-4 py-3">{{ t('contract_optimizer.invoice.value') }}</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="item in tableItems"
          :key="item.key"
          class="border-b-2 border-stroke"
          :class="{ 'bg-red-100': item.hasError && !item.isValueOnly && !isValueWithinErrorMargin(item.amount * item.pricePerUnit, item.valueFromAPI) }">
          <th scope="row" class="p-4 font-satoshi text-base font-medium text-black">
            {{ item.displayName }}
          </th>
          <td class="p-4">
            <input
              v-if="showItem(item.pricePerUnit, item.amount)"
              class="w-36 border-2 border-stroke px-1 font-satoshi text-sm font-medium text-body outline-none"
              type="number"
              v-model="item.amount"
              @change="updateFareItem(item.key, item.amount, item.pricePerUnit, item.valueFromAPI)" />
          </td>
          <td class="p-4">
            <input
              v-if="showItem(item.pricePerUnit, item.amount)"
              class="w-36 border-2 border-stroke px-1 font-satoshi text-sm font-medium text-body outline-none"
              type="number"
              v-model="item.pricePerUnit"
              @change="updateFareItem(item.key, item.amount, item.pricePerUnit, item.valueFromAPI)" />
          </td>
          <td
            class="p-4 font-satoshi text-base font-medium"
            :class="{
              'text-red-600': item.hasError && !item.isValueOnly && !isValueWithinErrorMargin(item.amount * item.pricePerUnit, item.valueFromAPI),
              'text-body': !item.hasError || item.isValueOnly || isValueWithinErrorMargin(item.amount * item.pricePerUnit, item.valueFromAPI),
            }">
            <div v-if="item.hasError && !item.isValueOnly && !isValueWithinErrorMargin(item.amount * item.pricePerUnit, item.valueFromAPI)">
              <div>{{ useCurrencyFormaterFromCents(item.amount * item.pricePerUnit) }}</div>
              <div class="text-xs">Esperado: {{ useCurrencyFormaterFromCents(item.valueFromAPI) }}</div>
            </div>
            <div v-else-if="item.hasError && item.isValueOnly">
              {{ useCurrencyFormaterFromCents(item.valueFromAPI) }}
            </div>
            <div v-else>
              {{ useCurrencyFormaterFromCents(item.amount * item.pricePerUnit) }}
            </div>
          </td>
        </tr>
        <tr v-if="tableItems.length === 0">
          <td colspan="4" class="p-4 text-center font-satoshi text-base font-medium text-body">Nenhum item de tarifa encontrado</td>
        </tr>
      </tbody>
      <tfoot v-if="tableItems.length > 0">
        <tr class="border-t-2 border-stroke bg-slate-50">
          <th scope="row" class="p-4 font-satoshi text-base font-bold text-black">Total</th>
          <td colspan="2" class="p-4"></td>
          <td class="p-4 font-satoshi text-base font-bold" :class="{ 'text-red-600': hasTotalError, 'text-black': !hasTotalError }">
            {{ useCurrencyFormaterFromCents(tariffTotalTable) }}
            <div v-if="hasTotalError" class="text-xs text-red-600">Esperado: {{ useCurrencyFormaterFromCents(tariffTotalAPI) }}</div>
          </td>
        </tr>
      </tfoot>
    </table>
  </div>
</template>
